import { TagIcon } from "@/pages/taskpane/components/icons/IconCustom";
import DivShadow from "@/pages/taskpane/components/layout/DivShadow";
import HStack from "@/pages/taskpane/components/layout/HStack";
import Select from "@/pages/taskpane/components/ui/Select";
import {
  useAppConfigStore,
  useAppointmentStore,
  useOutlookStore,
  useResourceStore,
  useThemeStore,
} from "@/pages/taskpane/shared/stores";
import { usePreSelectedResourceStore } from "@/pages/taskpane/shared/stores/preSelectedResourceStore";
import { EPreSelectedResource } from "@/pages/taskpane/shared/types/enums/preSelectedResourceEnum";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { saveMeetingType } from "../services/useSaveMeetingType";

type Props = {
  onRemoveNotify?: () => void;
  isDialog?: boolean;
};

const SelectMeetingType = ({ onRemoveNotify, isDialog = false }: Props) => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const { meetingLabel } = useAppConfigStore();
  const { meetingLabel: meetingLabelSelected, setMeetingLabel, selectedResource } = useResourceStore();
  const { iCalUId, customUid, organizer, organizerName, recurringDates, globalId } = useOutlookStore();
  const { startDateTimeUtc, endDateTimeUtc, timeZone } = useAppointmentStore();
  const { items } = usePreSelectedResourceStore();
  const resourcePending = useMemo(
    () => items?.find((item) => item.type === EPreSelectedResource.MeetingType)?.resources ?? [],
    [items]
  );

  const meetingLabelOptions = useMemo(
    () =>
      (meetingLabel?.meetingLabels || [])?.map((i) => ({
        label: i.name,
        value: i.id,
        color: i.color,
      })),
    [meetingLabel]
  );

  const handleChangeMeetingType = useCallback(
    (value: number) => {
      const listResource = [
        ...selectedResource,
        ...resourcePending.filter((r) => !selectedResource.some((s) => s.id === r.id)),
      ];
      if (!isDialog) {
        saveMeetingType({
          uid: customUid || "",
          resourcesIds: listResource.map((item) => item.id).join(","),
          // originalResourceIds: selectedResource.map((item) => item.id).join(","),
          meetingType: meetingLabelOptions.find((item) => item.value === value)?.label || "",
          isRecurring: recurringDates?.length > 0 ? 1 : 0,
          isNewMeeting: !iCalUId,
          timeZoneName: timeZone || "",
          organizerName: organizerName || "",
          organizerEmail: organizer || "",
          startDate: startDateTimeUtc || "",
          endDate: endDateTimeUtc || "",
          globalId: globalId || "",
          isSendMail: false,
          subject: "",
        });
      }
      setMeetingLabel(value);
      if (onRemoveNotify) {
        onRemoveNotify();
      }
    },
    [
      selectedResource,
      resourcePending,
      saveMeetingType,
      setMeetingLabel,
      onRemoveNotify,
      iCalUId,
      customUid,
      organizerName,
      organizer,
      startDateTimeUtc,
      endDateTimeUtc,
      timeZone,
      meetingLabelOptions,
    ]
  );

  return (
    <DivShadow className="bg-veryLightGray rounded-lg dark:bg-charcoalGray">
      <HStack className="px-4 py-3" justifyContent="between">
        <HStack gap={1}>
          <TagIcon isDarkMode={isDarkMode} />
          <p className="text-sm">{t("Meeting Type")}</p>
        </HStack>
        <div className="">
          <Select
            className="border-none text-sm font-semibold dark:bg-charcoalGray"
            options={meetingLabelOptions}
            value={meetingLabelSelected ?? undefined}
            onChange={(value) => handleChangeMeetingType(value as number)}
            placeholder={t("Meeting Label")}
            isHighlightSelected={false}
            alignTo="right"
          />
        </div>
      </HStack>
    </DivShadow>
  );
};

export default SelectMeetingType;
